import type { MarketAccount } from '@src/contexts/MarketAccountsContext';
import type { Aggregation } from '@src/types/Aggregation';
import type { Currency } from '@src/types/Currency';
import type { Customer } from '@src/types/Customer';
import type { CustomerSecurity } from '@src/types/CustomerSecurity';
import type { CustomerUser } from '@src/types/CustomerUser';
import type { AllInTier, FeeTier, SymbolGroup } from '@src/types/PricingTier';
import type { Security } from '@src/types/Security';
import {
  CustomerAddressTypeEnum,
  CustomerExecutionStrategyEnum,
  CustomerWorkflowEnum,
  ModeEnum,
  OrdTypeEnum,
  TimeInForceEnum,
  type ICustomerNetAcquisitionLimit,
  type MarketFeeModeEnum,
} from '@src/types/types';
import { prettyName } from '@src/utils/string';
import type { ReactNode } from 'react';
import type { SubAccount } from '../../../../types/SubAccount';
import type { EntityAdminRecord } from '../../types';
import { isNotDivider } from './components/dividerUtils';
import { flattenDrawerOptions } from './filterAndMapDrawerOptions';
import type {
  InputsAndDropdownsDrawerDropdownOption,
  InputsAndDropdownsDrawerOption,
  InputsAndDropdownsDrawerOptions,
} from './types';

/**
 * Transform helper for `transformControlToDrawerRecord` when an API field requires a JSON number.
 * Preserves empty string for cleared fields and correctly passes through numeric zero.
 *
 * @deprecated Reaching for this helper on a new API should be rare and treated as an exception,
 * similar to other EntityAdmin deprecated cases. If you need it for a new API field, prefer asking
 * the API developer to communicate the value as a string so EntityAdmin's string-in/string-out
 * numeric handling stays consistent.
 */
export const transformDrawerValueToNumber = (value: unknown): number | '' =>
  value === null || value === undefined || value === '' ? '' : Number(value);

/**
 * Resolves the tooltip value for a drawer option, handling function-based tooltips.
 * @param option - The drawer option containing the tooltip property
 * @param form - The form data to pass to the tooltip function if it's a function
 * @returns The resolved tooltip value (ReactNode or undefined)
 */
export function getTooltipValue<TDrawerRecord extends EntityAdminRecord>(
  option: InputsAndDropdownsDrawerOption<TDrawerRecord>,
  form: TDrawerRecord
): ReactNode | undefined {
  if (!isNotDivider(option)) {
    return undefined;
  }
  return typeof option.tooltip === 'function' ? option.tooltip(form) : option.tooltip;
}

export function getGuideTextValue<TDrawerRecord extends EntityAdminRecord>(
  option: InputsAndDropdownsDrawerOption<TDrawerRecord>,
  form: TDrawerRecord
): ReactNode | undefined {
  if (!isNotDivider(option)) {
    return undefined;
  }
  return typeof option.guideText === 'function' ? option.guideText(form) : option.guideText;
}

/**
 * Checks if any drawer option has guideText defined
 */
export function hasAnyGuideText<TDrawerRecord extends EntityAdminRecord>(
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>
): boolean {
  return flattenDrawerOptions(drawerOptions).some(option => isNotDivider(option) && option.guideText != null);
}

export function getCustomerUserDrawerOptions(
  customerUsers: Pick<CustomerUser, 'Email'>[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return customerUsers.map(({ Email }) => ({
    value: Email,
    label: Email, // Add preference to use DisplayName ?
  }));
}

/**
 * Returns an array of 24-hour time options in 15-minute intervals.
 */
export function getTimeDrawerOptions(): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return Array.from({ length: 24 }, (_, h) => {
    const hours = h < 10 ? `0${h}` : h;
    return ['00', '15', '30', '45']
      .map(minutes => `${hours}:${minutes}:00`)
      .map(time => ({ value: time, label: `${time} UTC` }));
  }).flat();
}

/**
 * Returns time options labelled with a custom time zone at minute granularity.
 */
export function getTimeDrawerOptionsWithTimezone(timeZone = 'UTC'): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return Array.from({ length: 24 * 60 }, (_, index) => {
    const totalMinutes = index;
    const hours = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, '0');
    const minutes = (totalMinutes % 60).toString().padStart(2, '0');
    const time = `${hours}:${minutes}:00`;

    return { value: time, label: `${time} ${timeZone}` };
  });
}

export function getCustomerDrawerOptions(
  customers: Pick<Customer, 'Name' | 'DisplayName'>[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return customers.map(({ Name, DisplayName }) => ({
    value: Name,
    label: DisplayName ?? Name,
  }));
}

export function getMarketAccountOptions(
  customerMarketAccounts: Pick<MarketAccount, 'Name' | 'DisplayName' | 'Counterparty' | 'SourceAccountID'>[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return customerMarketAccounts.map(({ Name, SourceAccountID, DisplayName }) => {
    return {
      value: Name,
      label: DisplayName ?? SourceAccountID,
    };
  });
}

export function getSecurityDrawerOptions(
  securities: Pick<CustomerSecurity | Security, 'Symbol' | 'DisplaySymbol' | 'Description'>[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return securities.map(({ Symbol, DisplaySymbol, Description }) => ({
    value: Symbol,
    label: DisplaySymbol ?? Symbol,
    description: Description,
  }));
}

export function getCurrencyDrawerOptions(
  currencies: Pick<Currency, 'Symbol' | 'Description'>[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return currencies.map(({ Symbol, Description }) => ({
    value: Symbol,
    label: Symbol,
    description: Description,
  }));
}

/**
 * Returns an array of common timezone options for dropdowns.
 * - While the Browser API supports all timezones in the world, this is a good subset to avoid overwhelming users with too many options.
 */
export function getTimezoneDrawerOptions(): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
    { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
    { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
    { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
    { value: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)' },
    { value: 'Europe/Madrid', label: 'Europe/Madrid (CET/CEST)' },
    { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET/CEST)' },
    { value: 'Europe/Zurich', label: 'Europe/Zurich (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
    { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT)' },
    { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
    { value: 'Asia/Seoul', label: 'Asia/Seoul (KST)' },
    { value: 'Asia/Mumbai', label: 'Asia/Mumbai (IST)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
    { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEST/AEDT)' },
    { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST/NZDT)' },
  ];
}

const timezoneDrawerOptionLabelByValue = new Map(
  getTimezoneDrawerOptions().map(option => [option.value, option.label])
);

/** Maps IANA timezone names to drawer options, using known labels from {@link getTimezoneDrawerOptions}. */
export function mapTimezoneNamesToDrawerOptions(
  timezoneNames: readonly string[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return timezoneNames.map(name => ({
    value: name,
    label: timezoneDrawerOptionLabelByValue.get(name) ?? name,
  }));
}

export function getSubAccountDrawerOptions(
  subAccounts: SubAccount[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return subAccounts.map(({ Name, DisplayName }) => ({
    value: Name,
    label: DisplayName ?? Name,
  }));
}

export function getAddressTypeDrawerOptions(): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return [
    { value: CustomerAddressTypeEnum.Deposit, label: 'Deposit' },
    { value: CustomerAddressTypeEnum.Withdrawal, label: 'Withdrawal' },
  ];
}

export function getOrdTypeDrawerOptions(): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return [
    {
      value: OrdTypeEnum.Limit,
      label: 'Limit',
    },
    {
      value: OrdTypeEnum.Market,
      label: 'Market',
    },
  ];
}

const FIXConnectionTypeEnum = {
  MarketData: 'MARKETDATA',
  Orders: 'ORDERS',
  DropCopy: 'DROPCOPY',
} as const;
type FIXConnectionTypeEnum = (typeof FIXConnectionTypeEnum)[keyof typeof FIXConnectionTypeEnum];

export function getConnectionTypeDrawerOptions(): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return [
    {
      value: FIXConnectionTypeEnum.Orders,
      label: 'Orders',
      description: 'Orders FIX Connection',
    },
    {
      value: FIXConnectionTypeEnum.DropCopy,
      label: 'Drop Copy',
      description: 'Post-Trade FIX Connection',
    },
    {
      value: FIXConnectionTypeEnum.MarketData,
      label: 'Market Data',
      description: 'MarketDataDepth and MarketDataTrades Connection Types',
    },
  ];
}

export function getExecutionStrategiesDrawerOptions(
  timeInForce: TimeInForceEnum
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  // If TIF != "FillOrKill", Only available execution strategy option is "RisklessPrincipal"
  if (timeInForce && timeInForce !== TimeInForceEnum.FillOrKill) {
    return [
      {
        value: CustomerExecutionStrategyEnum.RisklessPrincipal,
        label: 'Riskless Principal',
        description: 'Execute with a Cover and Deal Strategy',
      },
    ];
  }
  return [
    {
      value: CustomerExecutionStrategyEnum.OrderAcceptance,
      label: 'Order Acceptance',
      description: 'Execute Customer Order Unhedged',
    },
    {
      value: CustomerExecutionStrategyEnum.RisklessPrincipal,
      label: 'Riskless Principal',
      description: 'Execute with a Cover and Deal Strategy',
    },
  ];
}

export function getTimeInForceDrawerOptions(
  executionStrategy: CustomerExecutionStrategyEnum
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  // If ExecutionStrategy = OrderAcceptance, Only TIF option is "FillOrKill"
  if (executionStrategy === CustomerExecutionStrategyEnum.OrderAcceptance) {
    return [
      {
        value: TimeInForceEnum.FillOrKill,
        label: 'Fill or Kill',
      },
    ];
  }
  return [
    {
      value: TimeInForceEnum.FillOrKill,
      label: 'Fill or Kill',
    },
    {
      value: TimeInForceEnum.GoodTillCancel,
      label: 'Good Till Cancel',
    },
  ];
}

export function getWorkflowDrawerOptions(): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return [
    {
      value: CustomerWorkflowEnum.WhitelabelRFQ,
      label: 'Whitelabel RFQ',
      description: 'Order Actions Originating from the Whitelabel RFQ Workflow',
    },
    {
      value: CustomerWorkflowEnum.WhitelabelOrder,
      label: 'Whitelabel Order',
      description: 'Order Actions Originating from the Whitelabel Order Workflow',
    },
    {
      value: CustomerWorkflowEnum.CustomerAPIRFQ,
      label: 'Customer API RFQ',
      description: 'Order Actions Originating from the CustomerAPI RFQ Workflow',
    },
    {
      value: CustomerWorkflowEnum.CustomerAPIOrder,
      label: 'Customer API Order',
      description: 'Order Actions from the CustomerAPI Order Workflow',
    },
    {
      value: CustomerWorkflowEnum.SalesRFQ,
      label: 'Sales RFQ',
      description: 'Order Actions Originating from the Sales RFQ Workflow',
    },
    {
      value: CustomerWorkflowEnum.SalesOrder,
      label: 'Sales Order',
      description: 'Order Actions Originating from the Sales Orders Workflow',
    },
    {
      value: CustomerWorkflowEnum.DealerProxyRFQ,
      label: 'Dealer Proxy RFQ',
      description: 'Order Actions Originating from Non-Sales DealerProxy RFQ Workflow',
    },
    {
      value: CustomerWorkflowEnum.DealerProxyOrder,
      label: 'Dealer Proxy Order',
      description: 'Order Actions Originating from Non-Sales DealerProxy Order Workflow',
    },
    {
      value: CustomerWorkflowEnum.MobileWhitelabelRFQ,
      label: 'Mobile Whitelabel RFQ',
      description: 'Order Actions originating from the Mobile WL RFQ Workflow',
    },
    {
      value: CustomerWorkflowEnum.MobileWhitelabelOrder,
      label: 'Mobile Whitelabel Order',
      description: 'Order Actions originating from the Mobile WL Order Workflow',
    },
    {
      value: CustomerWorkflowEnum.CustomerFIXOrder,
      label: 'Customer FIX Order',
      description: 'Order Actions originating from the Customer FIX Order Workflow',
    },
    {
      value: CustomerWorkflowEnum.CustomerFIXRFQ,
      label: 'Customer FIX RFQ',
      description: 'Order Actions originating from the Customer FIX RFQ Workflow',
    },
  ];
}

export function getAggregationDrawerOptions(
  aggregations: Pick<Aggregation, 'Name' | 'DisplayName'>[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return aggregations.map(({ Name, DisplayName }) => ({
    value: Name,
    label: DisplayName ?? Name,
  }));
}

export function getFeeModeDrawerOptions(
  feeModeOptions: MarketFeeModeEnum[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return feeModeOptions.map(mode => ({ value: mode, label: prettyName(mode) }));
}

export function getTierDrawerOptions(
  tiers: Pick<AllInTier | FeeTier | SymbolGroup, 'Tier'>[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return tiers.map(({ Tier }) => ({ value: Tier, label: Tier }));
}

export function getModeDrawerOptions(): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return [
    { value: ModeEnum.Enabled, label: 'Enabled' },
    { value: ModeEnum.Disabled, label: 'Disabled' },
  ];
}

export function getCustomerNetAcquisitionLimitDrawerOptions(
  customerNetAcquisitionLimit: Pick<ICustomerNetAcquisitionLimit, 'Name'>[]
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return customerNetAcquisitionLimit.map(({ Name }) => {
    return {
      label: Name,
      value: Name,
    };
  });
}

export function getMarketAccountDrawerOptions(
  marketAccounts: Pick<MarketAccount, 'Name' | 'DisplayName' | 'Market'>[],
  marketDisplayNameByName?: Map<string, string>
): InputsAndDropdownsDrawerDropdownOption<string>[] {
  return marketAccounts.map(({ Name, DisplayName, Market }) => ({
    value: Name,
    label: DisplayName ?? Name,
    description: marketDisplayNameByName ? marketDisplayNameByName.get(Market) : undefined,
  }));
}
